import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Chip from '@mui/material/Chip';
import { withTheme } from '@mui/styles';
import { uniq } from 'lodash';
import { useStore } from 'common/store';
import { Fund } from 'common/types/fund';
import { UseStoreValues } from 'common/types/store';
import { toString } from 'utilities/toString';
import { FundsChipContainerProps } from './FundsChipContainer.props';
import { classes, StyledPaper } from './FundsChipContainer.styles';

const FundsChipContainer = ({ columns, setActiveFundsColumns, reset, theme }: FundsChipContainerProps) => {
  const [chipData, setChipData] = useState<Fund[]>([]);
  const [selectedFundIds, setSelectedFundIds] = useState<string[]>([]);
  const [{ captableInfo, fundList, fundInfo }] = useStore() as unknown as UseStoreValues;

  const funds = useMemo(() => {
    if (columns && captableInfo?.fund_ownership) {
      return uniq(columns.map(col => col.fund));
    }

    return [];
  }, [captableInfo, columns]);

  useEffect(() => {
    if (columns && fundList && captableInfo?.fund_ownership) {
      setChipData(
        fundList.filter(column =>
          captableInfo.fund_ownership?.fund_ownership_detail.map(ownership => ownership.fund).includes(column.id)
        )
      );
    } else if (fundInfo) {
      setChipData([fundInfo]);
    }
  }, [captableInfo, columns, fundList, funds, fundInfo]);

  useEffect(() => {
    if (columns) {
      const filteredColumns = columns.filter(column => !selectedFundIds.includes(toString(column.fund)));
      setActiveFundsColumns(filteredColumns);
    }
  }, [columns, setActiveFundsColumns, selectedFundIds]);

  useEffect(() => {
    if (reset) {
      setSelectedFundIds([]);
    }
  }, [reset]);

  const handleClick = useCallback(
    (selectedChip: Fund) => () => {
      if (!selectedFundIds.includes(toString(selectedChip.id)) && funds.length > selectedFundIds.length + 1) {
        setSelectedFundIds([...selectedFundIds, toString(selectedChip.id)]);
      } else if (selectedFundIds.includes(toString(selectedChip.id))) {
        setSelectedFundIds(selectedFundIds.filter(id => id !== toString(selectedChip.id)));
      }
    },
    [funds, selectedFundIds]
  );

  if (Array.isArray(chipData) && !chipData.length) {
    return null;
  }

  return (
    <StyledPaper component="ul" className={classes.root}>
      {chipData.map(data => (
        <li key={data.id}>
          <Chip
            id={toString(data.id)}
            label={data.name}
            onClick={handleClick(data)}
            className={classes.chip}
            style={
              selectedFundIds.includes(toString(data.id))
                ? {
                  backgroundColor: theme.palette.primary.contrastText,
                  color: theme.palette.primary.main,
                }
                : {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                }
            }
            clickable
          />
        </li>
      ))}
    </StyledPaper>
  );
};

export default withTheme(FundsChipContainer);
